import { effectiveHitPoints } from '../game/battle';
import { strategicWinProbability as attackerWinProbability } from '../game/combatOdds';
import { attacksFor } from '../game/movement';
import type { FactionId, GameContext, GameState, Piece } from '../game/types';

/** An attack another side could make next move and would gain by. */
export interface Threat {
  attacker: Piece;
  /** The threatened piece; the fight would be on its square. */
  target: Piece;
  /** The attacker's chance of winning that fight, 0 … 1. */
  probability: number;
  /** The attacker's expected gain in evaluation points: what it wins minus what it risks. Positive. */
  gain: number;
}

/** What a piece is worth to the evaluation: its tier and the hit points it has where it stands. */
export function pieceWorth(
  ctx: GameContext,
  state: GameState,
  piece: Piece,
  materialValues?: Readonly<Record<string, number>>,
): number {
  const hp = effectiveHitPoints(ctx, state, piece);
  const base = materialValues?.[piece.character];
  // Diagnostic opt-in: full-health values share one scale across the entire roster.
  // Never normalize factions separately or let invalid experimental values poison search.
  if (base !== undefined && Number.isFinite(base) && base > 0)
    return (base * hp) / Math.max(1, ctx.roster[piece.character]?.hitPoints ?? 1);
  return (ctx.roster[piece.character]?.tier ?? 1) * 10 + hp;
}

/**
 * Attacks the other sides could make on `faction`'s pieces next move that they would gain by.
 * An attack lost more often than it is won is no threat – it would not be made – so only the
 * attacker's expected gain (what they win minus what they risk) counts. This is the evaluation's
 * exposure term; the interface shares it so the board warns the player of exactly what the AI
 * fears. Threats come in enemy order, then destination order, for a stable evaluation sum; `by`
 * keeps one side's attacks only.
 */
export function threatsAgainst(
  ctx: GameContext,
  state: GameState,
  faction: FactionId,
  materialValues?: Readonly<Record<string, number>>,
  by?: FactionId,
): Threat[] {
  const threats: Threat[] = [];
  for (const enemy of state.pieces) {
    if (enemy.faction === faction || (by !== undefined && enemy.faction !== by)) continue;
    for (const d of attacksFor(ctx, state, enemy, faction)) {
      if (!d.attacks) continue;
      const probe: GameState = {
        ...state,
        phase: 'battle',
        activeFaction: enemy.faction,
        pendingBattle: { attackerId: enemy.id, defenderId: d.attacks.id, x: d.x, y: d.y },
      };
      const probability = attackerWinProbability(ctx, probe);
      const gain =
        probability * pieceWorth(ctx, state, d.attacks, materialValues) -
        (1 - probability) * pieceWorth(ctx, state, enemy, materialValues);
      if (gain > 0) threats.push({ attacker: enemy, target: d.attacks, probability, gain });
    }
  }
  return threats;
}
